import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useIsApiReady } from "../hooks/use-api-ready";
import {
  assignmentControllerAssignUsersToKit,
  assignmentControllerGetGroupAssignments,
  assignmentControllerUnassignUsersFromKit,
} from "../lib/gen-api";
import { queryKeys } from "../lib/query-keys";

/**
 * Get all assignments in a group
 */
export function useGroupAssignmentsData(groupUuid: string | null) {
  const {
    data: assignments,
    isPending,
    error,
  } = useQuery({
    enabled: useIsApiReady() && !!groupUuid,
    queryKey: queryKeys.assignments(groupUuid!),
    queryFn: () =>
      assignmentControllerGetGroupAssignments({
        path: {
          groupUuid: groupUuid!,
        },
      }).then((res) => res.data?.data ?? []),
  });

  return {
    assignments: assignments ?? [],
    isLoading: isPending,
    error,
  };
}

/**
 * Assign users to a kit
 */
export function useAssignUsersToKit(groupUuid: string | null) {
  const queryClient = useQueryClient();

  const { mutateAsync: assignUsers, isPending: isAssigning } = useMutation({
    mutationFn: ({
      kitUuid,
      userUuids,
    }: {
      kitUuid: string;
      userUuids: string[];
    }) => {
      if (!groupUuid) {
        throw new Error("Group UUID is required");
      }

      return assignmentControllerAssignUsersToKit({
        path: {
          groupUuid,
          kitUuid,
        },
        body: {
          user_uuids: userUuids,
        },
      }).then((res) => res.data);
    },
    onSuccess: (_, { kitUuid }) => {
      // Invalidate relevant queries
      queryClient.invalidateQueries({
        queryKey: queryKeys.assignments(groupUuid!),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.kitAssignments(groupUuid!, kitUuid),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.kits(groupUuid!),
      });
      toast.success("Users assigned successfully");
    },
    onError: (error: Error) => {
      toast.error("Failed to assign users", {
        description: error.message,
      });
    },
  });

  return {
    assignUsers,
    isAssigning,
  };
}

/**
 * Unassign users from a kit
 */
export function useUnassignUsersFromKit(groupUuid: string | null) {
  const queryClient = useQueryClient();

  const { mutateAsync: unassignUsers, isPending: isUnassigning } = useMutation({
    mutationFn: ({
      kitUuid,
      userUuids,
    }: {
      kitUuid: string;
      userUuids: string[];
    }) => {
      if (!groupUuid) {
        throw new Error("Group UUID is required");
      }

      return assignmentControllerUnassignUsersFromKit({
        path: {
          groupUuid,
          kitUuid,
        },
        body: {
          user_uuids: userUuids,
        },
      });
    },
    onSuccess: (_, { kitUuid }) => {
      // Invalidate relevant queries
      queryClient.invalidateQueries({
        queryKey: queryKeys.assignments(groupUuid!),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.kitAssignments(groupUuid!, kitUuid),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.kits(groupUuid!),
      });
      toast.success("Users unassigned successfully");
    },
    onError: (error: Error) => {
      toast.error("Failed to unassign users", {
        description: error.message,
      });
    },
  });

  return {
    unassignUsers,
    isUnassigning,
  };
}
